/**
 * Notification preferences: which agent categories may notify a user, and by which
 * channel.
 *
 * The categories are NOT listed here. They are the `category` values of the user's apps in
 * agenthub_master_menu — the same sections the Agent Hub shows — so a category added in
 * Mongo appears in Settings with no code change. Each is stored under a slug of its name
 * (`Fuel, Lube & Chemicals` → `fuel-lube-chemicals`), because names carry spaces, `&` and
 * `,` that make poor Mongo field names.
 *
 * Shared by the Settings page and /api/user-settings — deliberately with no DOM or Node
 * imports — so the key format, the defaults and the validation cannot drift between them.
 */

export const NOTIFICATION_CHANNELS = ['email', 'teams'] as const;

export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];

export type ChannelPreferences = Record<NotificationChannel, boolean>;

/** Stored preferences by category key. A category with nothing stored is absent. */
export type NotificationSettings = Record<string, ChannelPreferences>;

/** A PUT body's `notifications`: any subset of categories, any subset of channels. */
export type NotificationSettingsPatch = Record<string, Partial<ChannelPreferences>>;

export interface UserSettings {
  notifications: NotificationSettings;
}

/** What a category with nothing stored gets: email on, Teams off. */
export const DEFAULT_CHANNELS: ChannelPreferences = { email: true, teams: false };

/** A key is a slug: lower-case letters, digits and single hyphens. */
const KEY_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const MAX_KEY_LENGTH = 64;
/** Far above any real menu; a bound so one request cannot write an unbounded document. */
const MAX_CATEGORIES = 100;

const CHANNELS = new Set<string>(NOTIFICATION_CHANNELS);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** `Crew & Personnel` → `crew-personnel`. The stored key for a category name. */
export function categoryKey(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, MAX_KEY_LENGTH);
}

export function isCategoryKey(value: unknown): value is string {
  return typeof value === 'string' && value.length <= MAX_KEY_LENGTH && KEY_PATTERN.test(value);
}

/** One category's channels: what is stored, over the defaults. */
export function channelsFor(settings: NotificationSettings | null | undefined, key: string): ChannelPreferences {
  return { ...DEFAULT_CHANNELS, ...(settings?.[key] ?? {}) };
}

/**
 * The stored preferences, cleaned: well-formed keys only, boolean channels only. Anything
 * else in the document is dropped rather than trusted.
 */
export function sanitizeNotificationSettings(stored: unknown): NotificationSettings {
  const clean: NotificationSettings = {};
  if (!isRecord(stored)) return clean;
  for (const [key, value] of Object.entries(stored)) {
    if (!isCategoryKey(key) || !isRecord(value)) continue;
    clean[key] = {
      email: typeof value.email === 'boolean' ? value.email : DEFAULT_CHANNELS.email,
      teams: typeof value.teams === 'boolean' ? value.teams : DEFAULT_CHANNELS.teams,
    };
  }
  return clean;
}

/**
 * Validate a PUT body's `notifications`. Strict: one malformed key, unknown channel or
 * non-boolean value rejects the whole patch, so a client bug never half-applies.
 */
export function parseNotificationPatch(
  input: unknown,
): { patch: NotificationSettingsPatch; error?: undefined } | { patch?: undefined; error: string } {
  if (!isRecord(input)) return { error: 'notifications must be an object' };

  const entries = Object.entries(input);
  if (entries.length > MAX_CATEGORIES) return { error: `at most ${MAX_CATEGORIES} categories per request` };

  const patch: NotificationSettingsPatch = {};
  let fields = 0;
  for (const [key, value] of entries) {
    if (!isCategoryKey(key)) return { error: `"${key}" is not a valid category key` };
    if (!isRecord(value)) return { error: `"${key}" must be an object of channels` };

    const channels: Partial<ChannelPreferences> = {};
    for (const [channel, enabled] of Object.entries(value)) {
      if (!CHANNELS.has(channel)) return { error: `unknown channel "${channel}" in "${key}"` };
      if (typeof enabled !== 'boolean') return { error: `"${key}.${channel}" must be true or false` };
      channels[channel as NotificationChannel] = enabled;
      fields += 1;
    }
    patch[key] = channels;
  }

  if (fields === 0) return { error: 'notifications must change at least one channel' };
  return { patch };
}
